/**
 * Nursing completeness (1/0/NA) + consistency (focus/treatments) scoring.
 * Source of truth: copied into Cloud Functions before `tsc` (see scripts/copy-import-contract.cjs).
 */

export function roundCompletenessPct(score: number, full: number): number {
  return full ? Math.round((score / full) * 10000) / 100 : 0;
}

/** One checklist row: pass/fail/NA; NA excluded from denominator. */
export function completenessFromStringRow(row: string[]): { score: number; full: number; pct: number } {
  let score = 0;
  let full = 0;
  for (const raw of row) {
    const v = typeof raw === 'string' ? raw.trim() : String(raw ?? '');
    if (v === '1') score += 1;
    if (v === '0' || v === '1') full += 1;
  }
  return { score, full, pct: roundCompletenessPct(score, full) };
}

/**
 * Same rules as legacy countBinaryScore (numeric 0/1 from imports).
 * Use for Firestore payloads and mixed unknown[] columns.
 */
export function completenessFromUnknownRow(values: unknown[]): { score: number; full: number; pct: number } {
  let score = 0;
  let full = 0;
  for (const v of values) {
    if (v === '1' || v === 1) score += 1;
    if (v === '1' || v === 1 || v === '0' || v === 0) full += 1;
  }
  return { score, full, pct: roundCompletenessPct(score, full) };
}

export function aggregateCompletenessStringRows(rows: string[][]): { score: number; full: number; pct: number } {
  let score = 0;
  let full = 0;
  for (const row of rows) {
    const s = completenessFromStringRow(row);
    score += s.score;
    full += s.full;
  }
  return { score, full, pct: roundCompletenessPct(score, full) };
}

function toFiniteNum(val: unknown): number | null {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t || t === 'NA') return null;
    if (Number.isFinite(Number(t))) return Number(t);
  }
  return null;
}

/**
 * OR department: empty/non-positive focus/treatments become NA for consistency (matches ComplianceHub submit).
 */
export function resolveNursingFocusTreatmentsForOR(
  focus: string,
  treatments: string,
  isORDept: boolean,
): { focus: string; treatments: string } {
  if (!isORDept) {
    return { focus: focus || '', treatments: treatments || '' };
  }
  return {
    focus: !(Number(focus || 0) > 0) ? 'NA' : focus || '',
    treatments: !(Number(treatments || 0) > 0) ? 'NA' : treatments || '',
  };
}

/**
 * Per-chart consistency %; null when treatments <= 0 or non-numeric (incl. NA).
 */
export function perChartConsistencyPctFromStrings(focus: string, treatments: string): number | null {
  const f = toFiniteNum(focus);
  const t = toFiniteNum(treatments);
  if (f === null || t === null || t <= 0) return null;
  return Math.round((f / t) * 10000) / 100;
}

/**
 * Unweighted mean of per-chart consistency % (each value already 0–100).
 * Pass one slot per chart: number if `treatments > 0` and pct was computed, else `null`.
 * Charts with `treatments <= 0` or invalid focus/treatments must be `null` — they are **excluded**, not treated as 0%.
 * Returns null when no chart qualifies (omit `overall_consistency_pct` on documents; UI shows "—").
 */
export function overallConsistencyUnweightedAverage(pcts: Array<number | null>): number | null {
  const valid = pcts.filter((p): p is number => p !== null && Number.isFinite(p));
  if (!valid.length) return null;
  const sum = valid.reduce((a, b) => a + b, 0);
  /** Values are already 0–100 percents; two-decimal mean (not ratio×10000/100). */
  return Math.round((sum / valid.length) * 100) / 100;
}
